import { useMemo } from "react";
import { useSidebar } from "@/hooks/useSidebar";
import {
  PropsWithChildren,
  useState,
  useRef,
  useEffect,
  KeyboardEvent,
  type MouseEvent as ReactMouseEvent
} from "react";
import { SidebarProvider } from "./SidebarProvider";
import { showDevBanner } from "@/lib/urlUtils";
import NavigationRail from "./NavigationRail";
import { useLocation } from "react-router-dom";
import renderNavLinks from "./renderNavLinks";
import { Path } from "@/router";
import useCapabilities from "@/hooks/useCapabilities";
import { NavLink } from "../types";
import { defaultNavLinks } from "../navLinks";
import PinButton from "./PinButton";
import { useFlags } from "@/hooks/useFeatureFlags/useFlags";
import { compactTopActionBarStickyOffsetRem } from "@/components/TopActionBar/compactNavbarChrome";

type NavigationProps = {};

type OpenNavigationProps = PropsWithChildren<NavigationProps>;

const OpenNavigation = ({ children }: PropsWithChildren<OpenNavigationProps>) => {
  return (
    <NavigationRail>
      <div className="flex flex-col items-start self-stretch w-full pt-2 relative">
        <div className="flex items-center self-stretch pl-5 pr-3 h-[16px]"></div>
        <div className="flex flex-col items-start mt-3">
          <div className="flex w-[320px] px-3 flex-col items-start text-extended-pgadarknavy-brand-on-color">
            {children}
          </div>
        </div>
      </div>
    </NavigationRail>
  );
};

const ClosedNavigation = ({ children }: PropsWithChildren<NavigationProps>) => {
  return (
    <NavigationRail isOpen={false}>
      <div className="flex flex-col items-center relative">
        <div className="mt-9 text-extended-pgadarknavy-brand-on-color">{children}</div>
      </div>
    </NavigationRail>
  );
};

interface SidebarProps<P extends Path> {
  navLinks?: Record<string, NavLink<P>[]> | NavLink<P>[];
  bottomBar?: boolean;
}

function getScrollDirection(setIsScrolled: (isScrolled: boolean) => void) {
  const threshold = 10;
  let ticking = false;

  const updateScrollState = () => {
    const scrollY = window.scrollY;
    setIsScrolled(scrollY > threshold);
    ticking = false;
  };

  const onScroll = () => {
    if (!ticking) {
      window.requestAnimationFrame(updateScrollState);
      ticking = true;
    }
  };

  window.addEventListener("scroll", onScroll);

  return () => window.removeEventListener("scroll", onScroll);
}

const HOVER_CLOSE_DELAY_MS = 100;

type SidebarHoverOptions = {
  shouldSuppressOverlayClose?: () => boolean;
  /** Clears flyout pointer-inside suppress so Escape can collapse the hover overlay. */
  onEscapeDismiss?: () => void;
};

/**
 * Custom hook to encapsulate all hover-related logic for the sidebar.
 * Handles hover state, overlay focus, and suppression of overlay close after link clicks.
 */
function useSidebarHover(isSidebarOpen: boolean, options: SidebarHoverOptions = {}) {
  const { shouldSuppressOverlayClose, onEscapeDismiss } = options;
  const [isHovered, setIsHovered] = useState(false);
  const ignoreNextMouseLeave = useRef(false);
  const ignoreTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const overlayCloseTimeoutRef = useRef<number | null>(null);
  const hoverOverlayRef = useRef<HTMLDivElement>(null);

  // isHoverExpanded: true when sidebar is collapsed and hovered
  const isHoverExpanded = isHovered && !isSidebarOpen;

  // Focus overlay for accessibility when expanded
  useEffect(() => {
    if (isHoverExpanded && hoverOverlayRef.current) {
      hoverOverlayRef.current.focus();
    }
  }, [isHoverExpanded]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (ignoreTimeoutRef.current) clearTimeout(ignoreTimeoutRef.current);
      if (overlayCloseTimeoutRef.current) window.clearTimeout(overlayCloseTimeoutRef.current);
    };
  }, []);

  const clearOverlayCloseTimeout = () => {
    if (overlayCloseTimeoutRef.current !== null) {
      window.clearTimeout(overlayCloseTimeoutRef.current);
      overlayCloseTimeoutRef.current = null;
    }
  };

  // Always schedule; re-check flyout suppress inside the timeout so overlay→flyout
  // does not skip close entirely (which left the hover overlay stuck after flyout leave).
  const scheduleOverlayClose = () => {
    clearOverlayCloseTimeout();
    overlayCloseTimeoutRef.current = window.setTimeout(() => {
      if (shouldSuppressOverlayClose?.()) return;
      setIsHovered(false);
    }, HOVER_CLOSE_DELAY_MS);
  };

  // Mouse enter handler for both sidebar and overlay
  const handleMouseEnter = () => {
    clearOverlayCloseTimeout();
    setIsHovered(true);
  };

  // Mouse leave handler for base sidebar and outer wrapper
  const handleMouseLeave = (event: React.MouseEvent<HTMLDivElement>) => {
    const relatedTarget = event.relatedTarget;
    if (relatedTarget instanceof Node && hoverOverlayRef.current?.contains(relatedTarget)) {
      return;
    }

    scheduleOverlayClose();
  };

  // Mouse leave handler for overlay, with suppression logic
  const handleOverlayMouseLeave = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (ignoreNextMouseLeave.current) return;

    const relatedTarget = event.relatedTarget;
    if (relatedTarget instanceof Node && hoverOverlayRef.current?.contains(relatedTarget)) {
      return;
    }

    scheduleOverlayClose();
  };

  // Handler for when a link is clicked inside the overlay
  const handleOverlayLinkClick = () => {
    ignoreNextMouseLeave.current = true;
    if (ignoreTimeoutRef.current) clearTimeout(ignoreTimeoutRef.current);
    // Suppress overlay close for a short period after click
    ignoreTimeoutRef.current = setTimeout(() => {
      ignoreNextMouseLeave.current = false;
    }, 300);
  };

  // Keyboard handler for overlay
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      // Pointer-inside-flyout must not block Escape: clear suppress, then collapse.
      // Unmounting the overlay also removes the portaled flyout panel.
      onEscapeDismiss?.();
      setIsHovered(false);
    }
  };

  return {
    isHovered,
    isHoverExpanded,
    hoverOverlayRef,
    handleMouseEnter,
    handleMouseLeave,
    handleOverlayMouseLeave,
    handleOverlayLinkClick,
    handleKeyDown,
    setIsHovered,
    scheduleOverlayClose,
    clearOverlayCloseTimeout
  };
}

export default function Sidebar<P extends Path>({ navLinks }: PropsWithChildren<SidebarProps<P>>) {
  const { isSidebarOpen, setSidebarOpen } = useSidebar();
  const location = useLocation();
  const { capabilities } = useCapabilities();
  const flags = useFlags();
  const sidebarNavLinks = useMemo(() => defaultNavLinks(flags), [flags]);
  const flyoutPointerInsideRef = useRef(false);
  const scheduleOverlayCloseRef = useRef<() => void>(() => {});
  const clearOverlayCloseTimeoutRef = useRef<() => void>(() => {});
  const setFlyoutPointerInside = (inside: boolean) => {
    const wasInside = flyoutPointerInsideRef.current;
    flyoutPointerInsideRef.current = inside;
    if (inside) {
      // Entering the portaled flyout cancels any overlay→flyout close timer.
      clearOverlayCloseTimeoutRef.current();
      return;
    }
    // Leaving the flyout to page content must collapse the hover overlay.
    if (wasInside) {
      scheduleOverlayCloseRef.current();
    }
  };
  const shouldSuppressOverlayClose = () => flyoutPointerInsideRef.current;
  const clearFlyoutPointerInside = () => {
    flyoutPointerInsideRef.current = false;
  };

  // Apply new design styling
  const sidebarColor = "bg-extended-pgadarknavy-brand-color";

  // Use the custom hover hook
  const {
    isHoverExpanded,
    hoverOverlayRef,
    handleMouseEnter,
    handleMouseLeave,
    handleOverlayMouseLeave,
    handleOverlayLinkClick,
    handleKeyDown,
    setIsHovered,
    scheduleOverlayClose,
    clearOverlayCloseTimeout
  } = useSidebarHover(isSidebarOpen, {
    shouldSuppressOverlayClose,
    onEscapeDismiss: clearFlyoutPointerInside
  });
  scheduleOverlayCloseRef.current = scheduleOverlayClose;
  clearOverlayCloseTimeoutRef.current = clearOverlayCloseTimeout;
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    if (!showDevBanner) return;
    return getScrollDirection(setIsScrolled);
  }, []);

  function toggleSidebar() {
    setSidebarOpen(!isSidebarOpen);
    setIsHovered(false);
  }

  const pinButtonClasses = "absolute -top-4 left-5 z-20";

  const renderedNavLinks =
    navLinks &&
    (Array.isArray(navLinks)
      ? renderNavLinks({
          navLinks,
          domains: capabilities.domains,
          isLastGroup: true,
          location,
          isSidebarOpen,
          toggleSidebar: toggleSidebar
        })
      : Object.entries(navLinks).map(([groupTitle, links], index, array) =>
          renderNavLinks({
            domains: capabilities.domains,
            navLinks: links,
            groupTitle,
            isLastGroup: index === array.length - 1,
            location,
            isSidebarOpen,
            toggleSidebar: toggleSidebar
          })
        ));

  const renderedDefaultNavLinks = Object.entries(sidebarNavLinks).map(([groupTitle, links], index, array) =>
    renderNavLinks({
      domains: capabilities.domains,
      navLinks: links,
      groupTitle,
      isLastGroup: index === array.length - 1,
      location,
      isSidebarOpen,
      toggleSidebar: toggleSidebar
    })
  );

  const topOffset = `${compactTopActionBarStickyOffsetRem({ showDevBanner, isScrolled })}rem`;

  return (
    <SidebarProvider setFlyoutPointerInside={setFlyoutPointerInside}>
      <div className="relative h-full" onMouseLeave={handleMouseLeave}>
        <div
          className={`relative flex items-start sticky ml-4 pb-6 rounded-lg overflow-visible transition-all duration-200 ease-in-out ${
            sidebarColor ?? ""
          }`}
          style={{ top: topOffset, maxHeight: `calc(100vh - ${topOffset})` }}
          data-testid="sidebar"
          onMouseEnter={handleMouseEnter}
        >
          <div className="overflow-y-auto min-h-0 w-full max-h-full">
            {isSidebarOpen ? (
              <OpenNavigation>{renderedNavLinks}</OpenNavigation>
            ) : (
              <ClosedNavigation>{renderedNavLinks}</ClosedNavigation>
            )}
          </div>

          <div className={`${pinButtonClasses} ${isSidebarOpen ? "-rotate-45" : ""}`}>
            <PinButton toggleSidebar={toggleSidebar} />
          </div>
        </div>

        {isHoverExpanded && (
          <div
            ref={hoverOverlayRef}
            className={`fixed flex items-start pb-6 rounded-lg overflow-visible shadow-web-3 z-50 transition-all duration-200 ease-in-out outline-none ${sidebarColor}`}
            style={{ top: topOffset, left: "1rem", maxHeight: `calc(100vh - ${topOffset})` }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleOverlayMouseLeave}
            onClickCapture={(e) => {
              const target = e.target as HTMLElement;
              if (target.closest("a")) {
                handleOverlayLinkClick();
              }
            }}
            onKeyDown={handleKeyDown}
            tabIndex={-1}
          >
            <div className="overflow-y-auto min-h-0 w-full max-h-full">
              <SidebarProvider forceExpanded={true} setFlyoutPointerInside={setFlyoutPointerInside}>
                <OpenNavigation>{renderedNavLinks || renderedDefaultNavLinks}</OpenNavigation>
              </SidebarProvider>
            </div>

            <div className={pinButtonClasses}>
              <PinButton toggleSidebar={toggleSidebar} />
            </div>
          </div>
        )}
      </div>
    </SidebarProvider>
  );
}
